import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { supabase } from '../../services/supabase';
import { Send, Calculator } from 'lucide-react';

export const AdminCreateSignal: React.FC = () => {
    const [formData, setFormData] = useState({
        signal_type: 'BUY',
        entry: '',
        stop_loss: '',
        take_profit: '',
        destination: 'elite_53', // 'elite_53' | '313' | 'both'
        strategy_name: 'Admin Signal',
        tier: 'A',
        risk_rating: 'GREEN',
        reason: '',
        probability: 99
    });
    const [status, setStatus] = useState('');
    const [rrRatio, setRrRatio] = useState<number | null>(null);

    useEffect(() => {
        // Auto Calculate Risk/Reward
        const entry = parseFloat(formData.entry);
        const sl = parseFloat(formData.stop_loss);
        const tp = parseFloat(formData.take_profit);

        if (!isNaN(entry) && !isNaN(sl) && !isNaN(tp) && entry !== sl) {
            const risk = Math.abs(entry - sl);
            const reward = Math.abs(tp - entry);
            const rr = Math.abs(reward / risk);

            // Validate direction to ensure SL/TP logic makes sense
            if (formData.signal_type === 'BUY' && (sl >= entry || tp <= entry)) {
                setRrRatio(null); // Invalid Setup
            } else if (formData.signal_type === 'SELL' && (sl <= entry || tp >= entry)) {
                setRrRatio(null); // Invalid Setup
            } else {
                setRrRatio(parseFloat(rr.toFixed(2)));
            }
        } else {
            setRrRatio(null);
        }
    }, [formData.entry, formData.stop_loss, formData.take_profit, formData.signal_type]);

    const sendSignal = async (payload: any) => {
        const { data, error } = await supabase.functions.invoke('signals', {
            body: { signal: payload }
        });
        if (error || data?.error) {
            throw new Error(error?.message || data?.error || data?.message || "Failed to broadcast");
        }
        return data;
    };

    const submitSignal = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('Submitting...');

        try {
            const basePayload = {
                signal_type: formData.signal_type,
                entry: parseFloat(formData.entry),
                stop_loss: parseFloat(formData.stop_loss),
                take_profit: parseFloat(formData.take_profit),
                timeframe: 'Manual',
                zone_type: 'Manual',
                strategy_name: formData.strategy_name,
                strategy_id: `manual_${Date.now()}`,
                probability: formData.probability,
                tier: formData.tier,
                risk_rating: formData.risk_rating,
                reason: formData.reason || "Manual Intervention",
                rr_ratio: rrRatio
            };

            if (formData.destination === 'both') {
                await sendSignal({ ...basePayload, scanner_type: 'elite_53' });
                await sendSignal({ ...basePayload, scanner_type: '313' });
            } else {
                await sendSignal({ ...basePayload, scanner_type: formData.destination });
            }

            setStatus('Success! Signal Broadcasted & Telegram Fired.');
            setFormData({ ...formData, entry: '', stop_loss: '', take_profit: '', reason: '' });
        } catch (error: any) {
            console.error(error);
            setStatus(`Error: ${error.message}`);
        }
    };

    return (
        <AdminLayout>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">Manual Signal Generator</h1>
                <p className="text-gray-400">Push a custom signal to all users instantly.</p>
            </div>

            <div className="bg-finance-card border border-gray-800 rounded-xl p-6 max-w-2xl">
                <form onSubmit={submitSignal} className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Signal Type</label>
                            <select
                                value={formData.signal_type}
                                onChange={(e) => setFormData({ ...formData, signal_type: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                            >
                                <option value="BUY">BUY</option>
                                <option value="SELL">SELL</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Destination</label>
                            <select
                                value={formData.destination}
                                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                            >
                                <option value="elite_53">Elite Model (53)</option>
                                <option value="313">Standard Model (313)</option>
                                <option value="both">Both (Elite + Standard)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Entry Price</label>
                            <input required type="number" step="any" value={formData.entry} onChange={(e) => setFormData({ ...formData, entry: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Stop Loss</label>
                            <input required type="number" step="any" value={formData.stop_loss} onChange={(e) => setFormData({ ...formData, stop_loss: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Take Profit</label>
                            <input required type="number" step="any" value={formData.take_profit} onChange={(e) => setFormData({ ...formData, take_profit: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                                <Calculator className="w-4 h-4" /> Risk/Reward Ratio
                            </label>
                            <div className={`w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2 font-mono ${rrRatio !== null && rrRatio >= 1.5 ? 'text-emerald-400' : 'text-gray-500'}`}>
                                {rrRatio !== null ? `1 : ${rrRatio}` : 'Invalid SL/TP Setup'}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Strategy Name (Optional)</label>
                            <input type="text" value={formData.strategy_name} onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Quality Tier</label>
                            <select
                                value={formData.tier}
                                onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                            >
                                <option value="A">Tier A (Elite)</option>
                                <option value="B">Tier B (Premium)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Signal Probability (%)</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={formData.probability}
                                onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-400 mb-2">Reasoning / Analysis (Optional)</label>
                            <textarea
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white h-24 resize-none"
                                placeholder="Write the fundamental or technical reason for this signal..."
                            ></textarea>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 pt-4 border-t border-gray-800">
                        <button type="submit" disabled={rrRatio === null} className="bg-finance-accent hover:bg-yellow-500 text-finance-darker font-bold py-3 px-8 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <Send className="w-5 h-5" /> Broadcast Signal
                        </button>
                        <span className={status.includes('Error') ? 'text-red-400' : 'text-emerald-400'}>{status}</span>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};
